import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  ProviderSettingsItem,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import GitHubIcon from '@material-ui/icons/GitHub';
import { apis, githubEmuAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { utThemes } from './theme/utTheme';
import { UtWordmark } from './components/UtWordmark/UtWordmark';
import { Box, Typography } from '@material-ui/core';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

// added by backstage admins
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import { RbacPage } from '@backstage-community/plugin-rbac/legacy';

const app = createApp({
  themes: utThemes,
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        align="center"
        titleComponent={
          <div style={{ textAlign: 'center' }}>
            <UtWordmark />
            <Typography variant="h5">Sign in to Backstage</Typography>
            <Box
              border={1}
              borderColor="primary.main"
              borderRadius={4}
              p={2}
              mt={2}
              mb={1}
              mx="auto"
              maxWidth={560}
            >
              <Typography variant="body1">
                Please sign in with <strong>GitHub EMU</strong> unless you don't
                have an EMU account yet. GitHub Enterprise
                (github.austin.utexas.edu) is being decommissioned and shouldn't
                be used long term.
              </Typography>
            </Box>
          </div>
        }
        providers={[
          {
            id: 'github-emu-auth-provider',
            title: 'GitHub EMU (recommended)',
            message: 'Sign in using github.com (EMU)',
            apiRef: githubEmuAuthApiRef,
          },
          {
            id: 'github-ghe-auth-provider',
            title: 'GitHub Enterprise',
            message:
              'Only if you have no EMU account yet. Being decommissioned.',
            apiRef: githubAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/rbac" element={<RbacPage />} />;
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route
      path="/settings"
      element={
        <UserSettingsPage
          providerSettings={
            <>
              <ProviderSettingsItem
                title="GitHub Enterprise"
                description="github.austin.utexas.edu"
                icon={GitHubIcon}
                apiRef={githubAuthApiRef}
              />
              <ProviderSettingsItem
                title="GitHub EMU"
                description="github.com (EMU)"
                icon={GitHubIcon}
                apiRef={githubEmuAuthApiRef}
              />
            </>
          }
        />
      }
    />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
